// Deck schema migration, run once in the hydration path (TODO §2.2). Real decks are
// already persisted in IndexedDB, so every SlideData/DeckDocument shape change is a
// `schemaVersion` bump plus a migration here — never an in-place breaking read.
//
// v1 → v2: a grid reference lived directly on the slide as `griddDocId`; v2 replaced it
// with the discriminated `content` union (`content.ts`).
// v2 → v3: the single `content` window became `layout` + a `contents[]` array of 1–2
// windows (TODO §4.1). Grid *bodies* (griddSlice) and image assets are unchanged, so
// only the deck record migrates; a v1/v2 slide always becomes a `single`-layout slide.
// v3 → v4: slides gained optional `subtitle?`/`sticker?` (TODO §7.2/§7.3). Purely
// additive — an older slide has neither, so they simply read as `undefined`; the bump
// exists to document the shape change, there is no data transform.
// v4 → v5: the deck gained a top-level `transition` for presentation animations (TODO
// §10.1). Additive at the deck level — an older deck has none, so it defaults to
// `DEFAULT_TRANSITION` here; slides are untouched.
// v5 → v6: `meta.rev`, the storage layer's write counter for the single-writer rule (TODO
// §12.5.4). Existing decks start at rev 0 — every page reading such a deck agrees on that
// base, so the first write after the upgrade takes it to 1 and the comparison works from
// there without any special case.
// v6 → v7: slides gained the `cover` layout and optional `corners?` (TODO §19.0.1/§19.0.2).
// Purely additive: no existing slide is a cover and none has corners, so there is no data
// transform — the bump documents the shape change, exactly as v3 → v4 did.
// v7 → v8: the deck gained an optional `logo?` (TODO §24.0.1). Additive at the deck level;
// an older deck has none and renders no mark.
// v9 → v10: the `diagram` kind joined the `SlideContent` union (TODO §21/§4.1.1). Purely
// additive — no window written before this is a diagram — so, like v3 → v4 and v6 → v7,
// there is no data transform and the bump exists to record the shape change. The diagram
// BODIES live in their own `diagramDocs` store (§4.3.2) and migrate on read through
// Excalidraw's `restore()`, which is why no ladder for them appears here (§4.2.1).
// v10 → v11: the `gantt` kind joined the `SlideContent` union (TODO §26/§3.1). Purely
// additive, like v9 → v10 before it: no window written before this is a gantt, so there is no
// data transform and the bump exists to record the shape change. Gantt BODIES live in their
// own `ganttDocs` store (§3.3) and carry their own envelope `version`, which is why no ladder
// for them appears here.
// v8 → v9: slides gained `stickerTone?` (TODO §23.1.1). Additive, and the ABSENT case is the
// meaningful one: it means "the Sticker preset's own colour", so every sticker written before
// the redesign keeps a neutral pill rather than being assigned a status nobody chose.
// v11 → v12: slides gained `level?` — the pyramid (TODO §18.1.1/§18.1.5). Additive, and the
// ABSENT case is the whole point: absent reads as "storyline", so every deck already in
// storage is a valid ONE-LEVEL pyramid and nothing has to be assigned a level it never had.
// This is the one step below that does more than record a shape change, and it is still not a
// data transform: it runs `normalizePyramid` over `slides` so a record written by hand, by an
// older import path, or by a future reordering cannot arrive violating the two invariants
// (§18.1.3). It touches no field any other pending section adds, so it is order-independent
// with respect to them and a later merge must not try to serialise the three.
//
// v13 → v14: the `chart` kind joined the `SlideContent` union (TODO §15/§3.1.1). Purely
// additive, exactly like v9 → v10 and v10 → v11 before it: no window written before this is a
// chart, so there is no data transform and the bump records the shape change. Chart BODIES
// live in their own `chartDocs` store (§3.3) and carry their own envelope `version`, so no
// ladder for them appears here — and the chart STYLES live in the deck stylesheet (§6.2),
// which is Gridd's record stored verbatim and is not versioned by this counter at all.
//
// v14 → v15: `ImageContent.background?` — the colour an image window paints where the picture
// does not reach (TODO §22.5). Additive, and the absent case is the one every existing deck is
// in: absent means the default white, which is what `imageBackground` resolves it to, so no
// image written before this changes appearance. No data transform — the field rides through
// `migrateSlideToCurrent` inside `contents`, which is carried across wholesale.
//
// v12 → v13: slides gained `link?` — the knowledge-nugget link (TODO §17.1.4). Additive, and
// the absent case is again the ordinary one: a slide with no link is a slide this deck owns
// outright, which is every slide written before this. No data transform — but note what the
// bump does NOT do: it does not reconcile a link against its nugget. That happens in the
// hydration path (`reconcileNuggetLinks`, §17.2.5) and it has to, because it reads a second
// store and migration is a pure function of the deck record.
//
// **Both of these made every v6 deck run through `migrateSlideToCurrent` for the first
// time**, which is why that function now carries `splitRatio` and `corners` explicitly. It
// rebuilds each slide field by field rather than spreading — deliberately, so a legacy key
// cannot survive into a current record — and `splitRatio` (added after v3 without a bump,
// §4.7) was not in the list. Until this bump the omission was unreachable: a v6 deck
// returned early and never reached it. It would have silently reset every two-window
// slide to a 50/50 split on the first open after upgrading.

import { normalizePyramid } from "@/lib/pyramid";
import { DEFAULT_TRANSITION } from "@/lib/slide-transition";
import type { SlideContent } from "@/types/content";
import type {
  DeckDocument,
  DeckMeta,
  SlideData,
  SlideTransition,
} from "@/types/document";

const CURRENT_SCHEMA_VERSION = 15;

// A slide as persisted under schemaVersion 1: `griddDocId` on the slide, no `content`.
type LegacySlideV1 = {
  id: string;
  title: string;
  source: string;
  archived: boolean;
  griddDocId?: string | null;
};

// A slide as persisted under schemaVersion 2: a single discriminated `content` union,
// no `layout`/`contents`.
type LegacySlideV2 = {
  id: string;
  title: string;
  source: string;
  archived: boolean;
  content: SlideContent;
};

type AnySlide = SlideData | LegacySlideV2 | LegacySlideV1;

// The loaded record may predate any field the current types assume, so read it loosely.
// `transition` is omitted here too (absent on ≤ v4 decks) and defaulted during migration,
// as is `meta.rev` (absent on ≤ v5 decks).
type RawDeck = Omit<
  DeckDocument,
  "schemaVersion" | "slides" | "transition" | "meta"
> & {
  schemaVersion: number;
  slides: AnySlide[];
  transition?: SlideTransition;
  meta: Omit<DeckMeta, "rev"> & { rev?: number };
};

function migrateSlideToCurrent(slide: AnySlide): SlideData {
  const { id, title, source, archived } = slide;
  // Already v3+ (has layout + contents): carry through unchanged, including the optional
  // v4 subtitle/sticker, the unversioned splitRatio (§4.7) and the v7 corners — each
  // absent on an older slide, and each read as `undefined` there.
  if ("contents" in slide) {
    return {
      id,
      title,
      source,
      archived,
      layout: slide.layout,
      contents: slide.contents,
      subtitle: slide.subtitle,
      sticker: slide.sticker,
      stickerTone: slide.stickerTone,
      level: slide.level,
      link: slide.link,
      splitRatio: slide.splitRatio,
      corners: slide.corners,
    };
  }
  // v2: the single `content` union becomes the first (and only) window of a single layout.
  if ("content" in slide) {
    return {
      id,
      title,
      source,
      archived,
      layout: "single",
      contents: [slide.content],
    };
  }
  // v1: fold griddDocId into the union, then into that one-window single layout.
  const content: SlideContent = slide.griddDocId
    ? { kind: "gridd", griddDocId: slide.griddDocId }
    : null;
  return { id, title, source, archived, layout: "single", contents: [content] };
}

// Returns the deck at the current schema. Idempotent, and returns the input *by
// reference* when it is already current — so bootstrap can use identity to decide
// whether the upgraded record needs writing back to IndexedDB.
export function migrateDeck(deck: DeckDocument): DeckDocument {
  const raw = deck as unknown as RawDeck;
  if (raw.schemaVersion >= CURRENT_SCHEMA_VERSION) return deck;
  return {
    ...deck,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    // v11 → v12: the pyramid's invariants are established here, over slides that have just
    // been rebuilt field by field, so everything downstream — the outline, the numbering, the
    // group move — may assume them from the first render.
    slides: normalizePyramid(raw.slides.map(migrateSlideToCurrent)),
    // v4 → v5: default the deck transition when the record predates it (§10.1).
    transition: raw.transition ?? DEFAULT_TRANSITION,
    // v5 → v6: seed the write counter (§12.5.4).
    meta: { ...raw.meta, rev: raw.meta.rev ?? 0 },
    // v6 → v7 (cover/corners), v7 → v8 (logo), v8 → v9 (stickerTone) and v12 → v13 (link)
    // need no transform:
    // every field either already carries through `migrateSlideToCurrent` above or is absent
    // and optional.
    // `logo` rides along in the `...deck` spread.
  };
}
